// @flow
import { isSupported as hlsIsSupported } from 'hls.js';
import { selectServiceWorkerSupportedBrowsers, selectEncryptionEnabled } from './client';

/**
 * There are 3 types of players:
 * 1. (Preferred, if supported) MP3 Player (encrypted through service worker)
 * 2. (Only if MP3 is supported) Lossless/FLAC Player (encrypted through service worker)
 * 3. (Fallback) AAC/M3U8 Player (native or polyfilled, encrypted through HLS)
 */

function selectClientSupportsServiceWorker(): boolean {
  return typeof navigator !== 'undefined' && 'serviceWorker' in navigator;
}

/** 1. MP3 Player **/

export function selectClientSupportsMP3(state: Object): boolean {
  if (selectEncryptionEnabled(state)) {
    return selectClientSupportsServiceWorker() && selectServiceWorkerSupportedBrowsers(state);
  }
  return true;
}

/** 2. Lossless/FLAC Player **/

export function selectClientSupportsLossless(state: Object): boolean {
  if (selectEncryptionEnabled(state)) {
    return selectClientSupportsServiceWorker() && selectServiceWorkerSupportedBrowsers(state);
  }

  if (!window.Audio) {
    return false;
  }

  const canPlayType = new window.Audio().canPlayType('audio/flac');
  return canPlayType === 'probably' || canPlayType === 'maybe';
}

/** 3. AAC/M3U8 Player **/

export function selectClientSupportsNativeAAC(): boolean {
  const aacType = 'application/vnd.apple.mpegURL';
  const canPlayType =
    typeof document !== 'undefined' && document.createElement('video').canPlayType(aacType);

  return canPlayType === 'probably' || canPlayType === 'maybe';
}

export function selectClientSupportsPolyfilledAAC(): boolean {
  return typeof document !== 'undefined' && hlsIsSupported();
}

export function selectClientSupportsAAC(): boolean {
  return selectClientSupportsNativeAAC() || selectClientSupportsPolyfilledAAC();
}

export function selectClientSupportsPlayback(state: Object): boolean {
  return selectClientSupportsMP3(state) || selectClientSupportsAAC();
}

export function selectPlaybackIsUsingAAC(state: Object): boolean {
  return !selectClientSupportsMP3(state) && selectClientSupportsAAC();
}
